import { Random } from '../../utils/Random.js';

/**
 * Rare atmospheric world events (Phase 14) — e.g. DIGITAL GLITCH EVENT.
 */
export class WorldEventDirector {
  /**
   * @param {{
   *   config: import('../config.js').CONFIG['events'];
   *   getDeps: () => import('./WorldEventDirector.js').WorldEventDeps;
   * }} options
   */
  constructor({ config, getDeps }) {
    this.config = config;
    this._getDeps = getDeps;
    this._rng = new Random(config.seed);
    this._cooldown = this._rng.range(
      config.glitchIntervalMin,
      config.glitchIntervalMax,
    );
    this._active = false;
    this._remaining = 0;
    this._duration = 0;
    this._boost = 0;
  }

  get isGlitchEventActive() {
    return this._active;
  }

  /** @param {number} delta @param {{ inSimulation: boolean }} ctx */
  update(delta, ctx) {
    if (!this.config.enabled || !ctx.inSimulation) return;

    if (this._active) {
      this._remaining -= delta;
      const t = Math.max(0, this._remaining / this._duration);
      this._boost = t * this.config.glitchMaterialBoost;
      this._applyBoost(this._boost, t);
      if (this._remaining <= 0) {
        this._endEvent();
      }
      return;
    }

    this._cooldown -= delta;
    if (this._cooldown > 0) return;
    if (this._rng.next() > this.config.glitchChancePerCheck) {
      this._cooldown = this.config.glitchRetrySeconds;
      return;
    }
    this._startGlitchEvent();
  }

  _startGlitchEvent() {
    const deps = this._getDeps();
    this._active = true;
    this._duration = this._rng.range(
      this.config.glitchDurationMin,
      this.config.glitchDurationMax,
    );
    this._remaining = this._duration;
    this._boost = this.config.glitchMaterialBoost;

    deps.npcs?.setWorldFrozen(true);
    deps.rain?.setIntensityMultiplier(this.config.glitchRainMultiplier);
    deps.glitch?.forcePulse(this._duration, this.config.glitchScreenStrength);
    deps.ui?.flashWorldEvent('DIGITAL GLITCH EVENT');

    if (deps.audio?.started && deps.audio.ui) {
      deps.audio.ui.playGlitch();
    }

    this._applyBoost(this._boost, 1);
    this._cooldown = this._rng.range(
      this.config.glitchIntervalMin,
      this.config.glitchIntervalMax,
    );
  }

  _endEvent() {
    const deps = this._getDeps();
    this._active = false;
    this._boost = 0;
    deps.npcs?.setWorldFrozen(false);
    deps.rain?.setIntensityMultiplier(1);
    this._applyBoost(0, 0);
  }

  /** @param {number} boost @param {number} t01 */
  _applyBoost(boost, t01) {
    const deps = this._getDeps();
    deps.chunkMaterials?.setEventBoost(boost);
    deps.chunkMaterials?.setAccentFlicker(
      0.1 + boost * (0.35 + t01 * 0.25),
    );
    deps.atmosphere?.setEventBoost(boost);
    deps.flyingShips?.setEventBoost(boost * 0.85);
    deps.roadVehicles?.setEventBoost(boost * 0.75);
  }
}

/** @typedef {{
 *   chunkMaterials: { setEventBoost: (n: number) => void; setAccentFlicker: (n: number) => void } | null;
 *   atmosphere: import('../effects/AtmosphereEffects.js').AtmosphereEffects | null;
 *   flyingShips: import('../effects/FlyingShipFleet.js').FlyingShipFleet | null;
 *   roadVehicles: import('../effects/RoadVehicleTraffic.js').RoadVehicleTraffic | null;
 *   npcs: import('../npc/NPCManager.js').NPCManager | null;
 *   rain: import('../rendering/DigitalRain.js').DigitalRain | null;
 *   glitch: import('../rendering/GlitchController.js').GlitchController | null;
 *   ui: import('../ui/GameUI.js').GameUI | null;
 *   audio: import('../audio/AudioManager.js').AudioManager | null;
 * }} WorldEventDeps */
